/**
 * String 복사(Ctrl+drag / paste / divide) 저장·로딩 디버그
 * 콘솔 필터: [StringCopySave]
 * 끄기: localStorage.setItem('STRING_COPY_SAVE_DEBUG', '0')
 */
import { unwrapSolarStringApiPayload } from './panelSiteDesignPatch.js';

const PREFIX = '[StringCopySave]';

export function isStringCopySaveDebugEnabled() {
  if (typeof window === 'undefined') {
    return true;
  }
  try {
    return window.localStorage?.getItem('STRING_COPY_SAVE_DEBUG') !== '0';
  } catch {
    return true;
  }
}

export function logStringCopySave(phase, detail = {}) {
  if (!isStringCopySaveDebugEnabled()) {
    return;
  }
  const ts = typeof performance !== 'undefined' ? performance.now().toFixed(1) : Date.now();
  console.log(`${PREFIX} ${phase}`, { ts: `${ts}ms`, ...detail });
}

export function warnStringCopySave(phase, detail = {}) {
  if (!isStringCopySaveDebugEnabled()) {
    return;
  }
  console.warn(`${PREFIX} ${phase}`, detail);
}

export function errorStringCopySave(phase, detail = {}) {
  if (!isStringCopySaveDebugEnabled()) {
    return;
  }
  console.error(`${PREFIX} ${phase}`, detail);
}

/** vm / drawing ctx 에서 맵 로딩 오버레이 관련 플래그 스냅샷 */
export function snapshotMapLoadingFlags(source = {}) {
  return {
    isSavingSiteDesign: Boolean(source.isSavingSiteDesign),
    siteDesignSaveDepth: source.siteDesignSaveDepth ?? null,
    moduleTreeLoading: Boolean(source.moduleTreeLoading),
    isCreatingState: Boolean(source.isCreatingState),
    isUpdatingState: Boolean(source.isUpdatingState),
    isDeletingState: Boolean(source.isDeletingState),
    isDeleting: Boolean(source.isDeleting),
    isAddingArray: Boolean(source.isAddingArray)
  };
}

/** showMapLoadingOverlay 와 동일한 OR 조건 */
export function wouldShowMapLoadingOverlay(flags) {
  return Boolean(
    flags.isDeleting ||
      flags.isSavingSiteDesign ||
      flags.isAddingArray ||
      flags.isCreatingState ||
      flags.isUpdatingState ||
      flags.isDeletingState
  );
}

export function summarizeRawApiResult(raw) {
  if (raw == null) {
    return { kind: String(raw) };
  }
  if (typeof raw !== 'object') {
    return { kind: typeof raw, value: raw };
  }
  const keys = Object.keys(raw);
  const unwrapped = unwrapSolarStringApiPayload(raw);
  return {
    topLevelKeys: keys,
    topLevelId: raw.id ?? raw.seq ?? raw.solarStringSeq ?? null,
    hasContent: raw.content != null,
    hasData: raw.data != null,
    hasSolarString: raw.solarString != null,
    unwrappedKeys: unwrapped && typeof unwrapped === 'object' ? Object.keys(unwrapped) : [],
    unwrappedId: unwrapped?.id ?? unwrapped?.seq ?? unwrapped?.solarStringSeq ?? null,
    moduleCount:
      unwrapped?.solarModules?.length ??
      raw.solarModules?.length ??
      raw.content?.solarModules?.length ??
      null
  };
}

export async function traceStringCopySaveAsync(phase, fn, detailBefore = {}) {
  if (!isStringCopySaveDebugEnabled()) {
    return fn();
  }
  const t0 = performance.now();
  logStringCopySave(`${phase} → start`, detailBefore);
  try {
    const result = await fn();
    logStringCopySave(`${phase} → done`, {
      elapsedMs: (performance.now() - t0).toFixed(1),
      resultKind: result == null ? String(result) : typeof result,
      hasPatch: Boolean(result?.apiResult ?? result?.arraySeq)
    });
    return result;
  } catch (error) {
    errorStringCopySave(`${phase} → throw`, {
      elapsedMs: (performance.now() - t0).toFixed(1),
      message: error?.message,
      stack: error?.stack
    });
    throw error;
  }
}
